import {
  commandOutcomeSchema,
  CSRF_HEADER,
  CSRF_VALUE,
  IDEMPOTENCY_HEADER,
  type CommandOutcome,
} from '@personal-os/shared';

/** The server said no, with a reason the screens know how to explain. */
export class CommandApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Extra facts, e.g. the current copy after a version conflict. */
  readonly details: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'CommandApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** We kept trying and still couldn't reach the server. The same request ID can be retried. */
export class GaveUpError extends Error {
  readonly key: string;

  constructor(key: string) {
    super('still offline');
    this.name = 'GaveUpError';
    this.key = key;
  }
}

// Wait this long (in milliseconds) before each retry. After the last, we stop and say so.
export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 15000, 15000, 15000, 15000, 15000];
const RETRYABLE_STATUS = new Set([502, 503, 504]);

/** Waits for the delay, or until the browser says we're back online, whichever is first. */
function waitToRetry(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    const timer = window.setTimeout(done, ms);
    window.addEventListener('online', done, { once: true });
  });
}

interface SendOptions {
  /** One per user action. Retries reuse it, so the server runs the action only once. */
  key?: string;
  /** Called each time a try fails and we're about to wait and try again. */
  onWaiting?: (attempt: number) => void;
  delays?: readonly number[];
}

async function send(url: string, body: unknown, options: SendOptions): Promise<CommandOutcome> {
  const key = options.key ?? crypto.randomUUID();
  const delays = options.delays ?? RETRY_DELAYS_MS;

  for (let attempt = 0; ; attempt++) {
    let res: Response | null = null;
    try {
      res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          [CSRF_HEADER]: CSRF_VALUE,
          [IDEMPOTENCY_HEADER]: key,
        },
        body: JSON.stringify(body ?? {}),
      });
    } catch {
      // No connection: fall through to the retry below.
    }

    if (res && !RETRYABLE_STATUS.has(res.status)) {
      const json: unknown = await res.json().catch(() => null);
      if (res.ok) return commandOutcomeSchema.parse(json);
      const record = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
      const { error, message, ...details } = record;
      throw new CommandApiError(
        res.status,
        typeof error === 'string' ? error : 'server_error',
        typeof message === 'string' ? message : 'Something went wrong.',
        details,
      );
    }

    const delay = delays[attempt];
    if (delay === undefined) throw new GaveUpError(key);
    options.onWaiting?.(attempt + 1);
    await waitToRetry(delay);
  }
}

/** Runs a change (a "command"). Safe to retry: a dropped connection never makes it run twice. */
export function runCommand(type: string, input: unknown, options: SendOptions = {}) {
  return send(`/api/commands/${encodeURIComponent(type)}`, { input }, options);
}

/** Undoes an earlier change from History. */
export function undoChange(commandId: string, options: SendOptions = {}) {
  return send(`/api/history/${encodeURIComponent(commandId)}/undo`, {}, options);
}

/** Plain-language reason for anything that can go wrong with a change. */
export function describeCommandError(err: unknown): string {
  if (err instanceof GaveUpError)
    return 'Still offline. Check your connection, then try again. Nothing was saved twice.';
  if (err instanceof CommandApiError) {
    switch (err.code) {
      case 'version_conflict':
        return 'This changed since you opened it.';
      case 'not_found':
        return 'That no longer exists.';
      case 'undo_refused':
        return err.message;
      case 'unauthorized':
        return 'Your session ended. Sign in again.';
      case 'csrf':
        return "The request was blocked. Reload the page and make sure you're on the right address.";
      case 'validation_failed': {
        const issues = err.details.issues;
        const first = Array.isArray(issues)
          ? (issues[0] as { message?: unknown } | undefined)
          : undefined;
        return typeof first?.message === 'string'
          ? first.message
          : 'Some of the details are not valid.';
      }
      default:
        return 'Something went wrong. Please try again.';
    }
  }
  return 'Something went wrong. Please try again.';
}
